import React from 'react';
import { ParkingSpaceWithContract, PARKING_STATUS_LABELS, PARKING_STATUS_COLORS } from '../../../types/parkingSpace';

interface ParkingSpaceTableProps {
  spaces: ParkingSpaceWithContract[];
  onEdit: (space: ParkingSpaceWithContract) => void;
  onDelete: (spaceId: string) => void;
  loading: boolean;
}

const ParkingSpaceTable: React.FC<ParkingSpaceTableProps> = ({ 
  spaces, 
  onEdit, 
  onDelete, 
  loading 
}) => {
  // 만료일 계산
  const getDaysUntilExpiry = (endDate: Date) => {
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 만료일 표시 스타일
  const getExpiryStyle = (endDate: Date) => {
    const daysUntilExpiry = getDaysUntilExpiry(endDate);
    
    if (daysUntilExpiry < 0) {
      return 'text-red-600 font-semibold'; // 만료됨
    } else if (daysUntilExpiry <= 7) {
      return 'text-red-500 font-medium'; // 7일 이하
    } else if (daysUntilExpiry <= 30) {
      return 'text-yellow-600 font-medium'; // 30일 이하
    }
    return 'text-gray-600'; // 정상
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 text-center">
          <div className="text-lg text-gray-500">로딩 중...</div>
        </div>
      </div>
    );
  }

  if (spaces.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 text-center">
          <div className="text-lg text-gray-500">주차면이 없습니다.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                주차면번호
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                이용자 정보
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                이용기간
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                차량정보
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                만료까지
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                작업
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {spaces.map((space) => (
              <tr key={space.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {space.spaceNumber}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${PARKING_STATUS_COLORS[space.status]}`}>
                    {PARKING_STATUS_LABELS[space.status]}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {space.currentContract?.userInfo ? (
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{space.currentContract.userInfo.name}</div>
                      <div className="text-gray-500">{space.currentContract.userInfo.phoneNumber}</div>
                      <div className="text-gray-500">{space.currentContract.userInfo.organization}</div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">-</div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {space.currentContract?.usagePeriod ? (
                    <div className="text-sm text-gray-900">
                      <div>{space.currentContract.usagePeriod.startDate.toLocaleDateString('ko-KR')}</div>
                      <div>~</div>
                      <div>{space.currentContract.usagePeriod.endDate.toLocaleDateString('ko-KR')}</div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">-</div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {space.currentContract?.vehicleInfo ? (
                    <div className="text-sm text-gray-900">{space.currentContract.vehicleInfo}</div>
                  ) : (
                    <div className="text-sm text-gray-400">-</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {space.currentContract?.usagePeriod?.endDate ? (
                    <div className={`text-sm ${getExpiryStyle(space.currentContract.usagePeriod.endDate)}`}>
                      {(() => {
                        const daysUntilExpiry = getDaysUntilExpiry(space.currentContract.usagePeriod.endDate);
                        if (daysUntilExpiry < 0) {
                          return `${Math.abs(daysUntilExpiry)}일 만료됨`;
                        } else if (daysUntilExpiry === 0) {
                          return '오늘 만료';
                        } else {
                          return `${daysUntilExpiry}일 남음`;
                        }
                      })()}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">-</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => onEdit(space)}
                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => onDelete(space.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ParkingSpaceTable; 